import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Trash2 } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { toast } from 'sonner';

const EMPTY = {
  code: '',
  kind: 'company',
  company_name: '',
  registration_number: '',
  contact_person: '',
  phone: '',
  email: '',
  area_of_operation: '',
  services_provided: [],
  rate_card: [],
  banking_details: '',
  personnel: [],
};

const KIND_OPTIONS = [
  { value: 'company',  label: 'Company',  hint: 'External 3rd-party contractor (eg. Phase3 Extras)' },
  { value: 'employee', label: 'Employee', hint: 'In-house staff member dispatched directly' },
  { value: 'vehicle',  label: 'Vehicle',  hint: 'Company-owned response unit — registration is the identifier' },
];

const KIND_STYLE = {
  company:  'bg-[#0D5B69]/10 text-[#0D5B69] border-[#0D5B69]/20',
  employee: 'bg-amber-100 text-amber-800 border-amber-200',
  vehicle:  'bg-[#80C030]/15 text-[#5a8c20] border-[#80C030]/30',
};

export const ContractorDialog = ({ open, onOpenChange, contractor, services, onSaved }) => {
  const isEdit = Boolean(contractor?.id);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(contractor ? {
        ...EMPTY,
        ...contractor,
        services_provided: contractor.services_provided || [],
        rate_card: contractor.rate_card || [],
        personnel: contractor.personnel || [],
      } : EMPTY);
    }
  }, [open, contractor]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleService = (svcId) => {
    setForm((s) => {
      const has = s.services_provided.includes(svcId);
      const newServices = has ? s.services_provided.filter((id) => id !== svcId) : [...s.services_provided, svcId];
      const newRateCard = has
        ? s.rate_card.filter((r) => r.service_id !== svcId)
        : [...s.rate_card, { service_id: svcId, billing_type: 'per_hour', rate: 0, minimum_units: 0 }];
      return { ...s, services_provided: newServices, rate_card: newRateCard };
    });
  };

  const updateRate = (svcId, field, value) => {
    const numeric = ['rate', 'minimum_units', 'base_fee', 'included_hours', 'overflow_rate'];
    setForm((s) => ({
      ...s,
      rate_card: s.rate_card.map((r) =>
        r.service_id === svcId
          ? { ...r, [field]: numeric.includes(field) ? Number(value || 0) : value }
          : r
      ),
    }));
  };

  const updateRateFields = (svcId, patch) => {
    setForm((s) => ({
      ...s,
      rate_card: s.rate_card.map((r) => (r.service_id === svcId ? { ...r, ...patch } : r)),
    }));
  };

  // The Billing Type select doubles as the cost model picker.
  const rateModel = (r) => (r?.billing_model === 'flat_plus_hourly'
    ? 'flat_plus_hourly' : (r?.billing_type || 'per_hour'));

  const setRateModel = (svcId, v) => updateRateFields(svcId, v === 'flat_plus_hourly'
    ? { billing_model: 'flat_plus_hourly', billing_type: 'per_hour' }
    : { billing_model: 'standard', billing_type: v });

  const addPerson = () =>
    setForm((s) => ({
      ...s,
      personnel: [...s.personnel, { name: '', phone: '', role: '', vehicle_registration: '', vehicle_make_model: '', active: true }],
    }));

  const updatePerson = (idx, field, value) =>
    setForm((s) => ({
      ...s,
      personnel: s.personnel.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));

  const removePerson = (idx) =>
    setForm((s) => ({ ...s, personnel: s.personnel.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.company_name.trim()) {
      const labelByKind = { employee: 'Employee name', vehicle: 'Vehicle make/model' };
      toast.error(`${labelByKind[form.kind] || 'Company name'} is required`);
      return;
    }
    if (form.kind === 'vehicle' && !form.registration_number?.trim()) {
      toast.error('Vehicle registration is required for vehicle contractors');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' && k !== 'company_name') delete payload[k];
      });
      // strip out incomplete personnel rows
      payload.personnel = (payload.personnel || []).filter((p) => p.name?.trim());

      if (isEdit) {
        await api.patch(`/contractors/${contractor.id}`, payload);
        toast.success('Contractor updated');
      } else {
        await api.post('/contractors', payload);
        toast.success('Contractor created');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="contractor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0D5B69]">
            {isEdit ? 'Edit Contractor' : 'New Contractor'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? `Updating ${contractor.company_name}` : 'Add a new contractor to the operational roster'}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="mt-2">
          <TabsList>
            <TabsTrigger value="details">Company</TabsTrigger>
            <TabsTrigger value="rates">Rate Card</TabsTrigger>
            <TabsTrigger value="personnel">Personnel</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-3 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Contractor Type *</Label>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" data-testid="contractor-kind-picker">
                {KIND_OPTIONS.map((k) => (
                  <button
                    key={k.value}
                    type="button"
                    role="radio"
                    aria-checked={form.kind === k.value}
                    onClick={() => set('kind', k.value)}
                    className={`text-left p-3 rounded-md border-2 transition-all ${
                      form.kind === k.value
                        ? 'border-[#80C030] bg-[#80C030]/5 ring-2 ring-[#80C030]/20'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                    data-testid={`contractor-kind-${k.value}`}
                  >
                    <div className="text-sm font-bold text-slate-900">{k.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{k.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Contractor Code">
                <Input value={form.code || ''} onChange={(e) => set('code', e.target.value)} placeholder={form.kind === 'employee' ? 'EMP-001' : form.kind === 'vehicle' ? 'VEH-001' : 'CON-001'} />
              </Field>
              <Field label={form.kind === 'employee' ? 'Employee Name *' : form.kind === 'vehicle' ? 'Vehicle Make / Model *' : 'Company Name *'}>
                <Input
                  value={form.company_name}
                  onChange={(e) => set('company_name', e.target.value)}
                  placeholder={form.kind === 'employee' ? 'eg. John Mokoena' : form.kind === 'vehicle' ? 'eg. Toyota Hilux DC' : 'eg. Phase3 Extras Pty Ltd'}
                  required
                  data-testid="contractor-company-name-input"
                />
              </Field>
              <Field label={form.kind === 'vehicle' ? 'Vehicle Registration *' : form.kind === 'employee' ? 'Employee ID' : 'Registration No'}>
                <Input
                  value={form.registration_number || ''}
                  onChange={(e) => set('registration_number', e.target.value.toUpperCase())}
                  placeholder={form.kind === 'vehicle' ? 'eg. CA 123-456' : form.kind === 'employee' ? 'eg. ID 8501010012084' : 'eg. 2019/123456/07'}
                  required={form.kind === 'vehicle'}
                  data-testid="contractor-registration-input"
                  className={form.kind === 'vehicle' ? 'font-mono uppercase tracking-wider' : ''}
                />
              </Field>
              <Field label={form.kind === 'employee' ? 'Direct Phone' : 'Contact Person'}>
                <Input
                  value={form.contact_person || ''}
                  onChange={(e) => set('contact_person', e.target.value)}
                  placeholder={form.kind === 'employee' ? 'Not applicable' : form.kind === 'vehicle' ? 'Allocated driver name' : 'eg. John Smith'}
                  disabled={form.kind === 'employee'}
                />
              </Field>
              <Field label="Phone">
                <Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="+27 ..." />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
              </Field>
              <Field label="Area of Operation" className="col-span-2">
                <Input value={form.area_of_operation || ''} onChange={(e) => set('area_of_operation', e.target.value)} placeholder="eg. Johannesburg North & Sandton" />
              </Field>
            </div>
            <Field label={form.kind === 'vehicle' ? 'Asset / Fleet Notes' : 'Banking Details'}>
              <Textarea
                rows={3}
                value={form.banking_details || ''}
                onChange={(e) => set('banking_details', e.target.value)}
                placeholder={form.kind === 'vehicle' ? 'eg. Service interval, tracker ID, fuel card #' : 'Bank · Account No · Branch — used for contractor payments'}
              />
            </Field>
          </TabsContent>

          <TabsContent value="rates" className="space-y-3 mt-4">
            <p className="text-xs text-slate-500">
              Tick each service this contractor provides, then set the cost rate (what CSG pays them).
              All rates <strong>exclude VAT</strong>.
            </p>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left p-2.5 font-semibold w-8"></th>
                    <th className="text-left p-2.5 font-semibold">Service</th>
                    <th className="text-left p-2.5 font-semibold w-32">Billing Type</th>
                    <th className="text-left p-2.5 font-semibold w-28">Cost Rate</th>
                    <th className="text-left p-2.5 font-semibold w-24">Min Units</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((svc) => {
                    const provided = form.services_provided.includes(svc.id);
                    const rate = form.rate_card.find((r) => r.service_id === svc.id);
                    const isFlat = rateModel(rate) === 'flat_plus_hourly';
                    return (
                      <React.Fragment key={svc.id}>
                      <tr className="border-t border-slate-100">
                        <td className="p-2.5">
                          <Checkbox
                            checked={provided}
                            onCheckedChange={() => toggleService(svc.id)}
                            data-testid={`service-toggle-${svc.code}`}
                          />
                        </td>
                        <td className="p-2.5">
                          <p className={`font-medium text-sm ${provided ? 'text-slate-900' : 'text-slate-400'}`}>{svc.name}</p>
                        </td>
                        <td className="p-2.5">
                          {provided && (
                            <Select
                              value={rateModel(rate)}
                              onValueChange={(v) => setRateModel(svc.id, v)}
                            >
                              <SelectTrigger className="h-8" data-testid={`rate-type-${svc.code}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per_hour">Per Hour</SelectItem>
                                <SelectItem value="pro_rata">Pro Rata</SelectItem>
                                <SelectItem value="fixed">Fixed</SelectItem>
                                <SelectItem value="flat_plus_hourly">Flat + Hourly</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="p-2.5">
                          {provided && !isFlat && (
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="h-8"
                              value={rate?.rate || ''}
                              onChange={(e) => updateRate(svc.id, 'rate', e.target.value)}
                              placeholder="0.00"
                              data-testid={`rate-amount-${svc.code}`}
                            />
                          )}
                          {provided && isFlat && (
                            <span className="text-xs text-slate-400">see below</span>
                          )}
                        </td>
                        <td className="p-2.5">
                          {provided && !isFlat && (
                            <Input
                              type="number"
                              min={0}
                              step="0.25"
                              className="h-8"
                              value={rate?.minimum_units || ''}
                              onChange={(e) => updateRate(svc.id, 'minimum_units', e.target.value)}
                              placeholder="0"
                            />
                          )}
                        </td>
                      </tr>
                      {provided && isFlat && (
                        <tr className="bg-teal-50/60">
                          <td></td>
                          <td colSpan={4} className="px-2.5 pb-3 pt-1">
                            <div className="grid grid-cols-4 gap-2">
                              <div>
                                <label className="text-[10px] uppercase tracking-wide text-slate-500">Flat cost (R)</label>
                                <Input
                                  type="number" min={0} step="0.01" className="h-8"
                                  value={rate?.base_fee || ''}
                                  onChange={(e) => updateRate(svc.id, 'base_fee', e.target.value)}
                                  placeholder="750.00"
                                  data-testid={`rate-base-fee-${svc.code}`}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wide text-slate-500">Covers first (hrs)</label>
                                <Input
                                  type="number" min={0} step="0.25" className="h-8"
                                  value={rate?.included_hours || ''}
                                  onChange={(e) => updateRate(svc.id, 'included_hours', e.target.value)}
                                  placeholder="3"
                                  data-testid={`rate-included-hours-${svc.code}`}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wide text-slate-500">Then per hour (R)</label>
                                <Input
                                  type="number" min={0} step="0.01" className="h-8"
                                  value={rate?.overflow_rate || ''}
                                  onChange={(e) => updateRate(svc.id, 'overflow_rate', e.target.value)}
                                  placeholder="500.00"
                                  data-testid={`rate-overflow-${svc.code}`}
                                />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wide text-slate-500">Effective from</label>
                                <Input
                                  type="date" className="h-8"
                                  value={rate?.effective_from || ''}
                                  onChange={(e) => updateRate(svc.id, 'effective_from', e.target.value)}
                                  data-testid={`rate-effective-from-${svc.code}`}
                                />
                              </div>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1.5">
                              Cost is the flat amount up to {rate?.included_hours || 0}h, then
                              {' '}R{Number(rate?.overflow_rate || 0).toLocaleString('en-ZA')}/h pro-rata.
                              {' '}<b>The hourly amount also governs cases opened before the effective date</b> —
                              leave it at the correct rate, never at 0.
                            </p>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="personnel" className="space-y-3 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Team members operating under this contractor.</p>
              <Button size="sm" variant="outline" onClick={addPerson} className="gap-1" data-testid="add-person-btn">
                <Plus className="w-3.5 h-3.5" /> Add Person
              </Button>
            </div>
            {form.personnel.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-md">
                No personnel added yet
              </p>
            ) : (
              <div className="space-y-2">
                {form.personnel.map((p, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`person-${idx}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Full name" value={p.name} onChange={(e) => updatePerson(idx, 'name', e.target.value)} />
                      <Input placeholder="Phone" value={p.phone || ''} onChange={(e) => updatePerson(idx, 'phone', e.target.value)} />
                      <Input placeholder="Role (e.g. Reaction Officer)" value={p.role || ''} onChange={(e) => updatePerson(idx, 'role', e.target.value)} />
                      <Input placeholder="Vehicle reg (e.g. GP 123 ABC)" value={p.vehicle_registration || ''} onChange={(e) => updatePerson(idx, 'vehicle_registration', e.target.value)} />
                      <Input placeholder="Vehicle make/model" className="col-span-2" value={p.vehicle_make_model || ''} onChange={(e) => updatePerson(idx, 'vehicle_make_model', e.target.value)} />
                    </div>
                    <div className="flex justify-end">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 gap-1" onClick={() => removePerson(idx)} data-testid={`remove-person-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold"
            data-testid="save-contractor-btn"
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Contractor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</Label>
    {children}
  </div>
);
